import { CSSProperties, useCallback, useMemo, useRef } from "react";

import useCanManageApiKeys from "shared/ApiKey/access/useCanManageApiKeys";
import TableContextProvider from "components/Table/Context";
import Table from "components/Table";
import TableRow from "components/Table/Row";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import useTableSort from "components/Table/useTableSort";
import SearchInput from "components/SearchInput";
import TableWrapper from "components/Table/Wrapper";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored, InfoCircle } from "components/icons/generated";
import useTypedContext from "hooks/useTypedContext";
import ButtonIcon from "ds/components/ButtonIcon";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import { ApiKeyGroupMappingEntity } from "./types";
import useGetApiKeyGroups from "./useGetApiKeyGroups";
import {
  initialSortDirection,
  initialSortOption,
  API_KEY_GROUP_MAPPING_COLUMNS_STORAGE_KEY,
  ROW_HEADER_ID,
  SORT_CONFIG,
  FILTERS_ORDER_SETTINGS_KEY,
  SIMPLE_COLUMN_CONFIG,
  EXTENDED_COLUMN_CONFIG,
  API_KEY_GROUP_MAPPING_COLUMN_ID,
} from "./constants";
import ApiKeyGroupMappingEmpty from "./Empty";
import ApiKeyGroupMappingListItem from "./ListItem";
import { useFilteredEntities } from "./useFilteredEntities";
import { SettingsContext } from "../../Context";
import { UserManagementActivationStatus } from "../../types";
import { ApiKeyView } from "../types";
import { showApiKeyGroupMappingInfoDrawer } from "./InfoDrawer";
import { showApiKeysManageGroupsDrawer } from "../ManageGroupsDrawer";

type ApiKeyGroupMappingProps = {
  apiKey: ApiKeyView;
};

const ApiKeyGroupMapping = ({ apiKey }: ApiKeyGroupMappingProps) => {
  const { groups, loading } = useGetApiKeyGroups({ apiKeyTeams: apiKey.teams });
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const canManageApiKeys = useCanManageApiKeys();

  const { activationStatus } = useTypedContext(SettingsContext);

  const columnsViewConfig = useMemo(() => {
    if (activationStatus === UserManagementActivationStatus.ACTIVE) {
      return EXTENDED_COLUMN_CONFIG;
    }

    return SIMPLE_COLUMN_CONFIG;
  }, [activationStatus]);

  const initialColumnsViewConfig = useMemo(() => {
    return {
      visible: Object.keys(columnsViewConfig) as API_KEY_GROUP_MAPPING_COLUMN_ID[],
      hidden: [],
    };
  }, [columnsViewConfig]);

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const filteredGroups = useFilteredEntities(groups, sortDescriptor);
  const isPageEmpty = filteredGroups.length === 0;

  const renderRow = useCallback(
    (
      item: Partial<ApiKeyGroupMappingEntity> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <ApiKeyGroupMappingListItem group={item as ApiKeyGroupMappingEntity} apiKey={apiKey} />
        </TableRow>
      );
    },
    [apiKey]
  );

  const openInfoDrawer = useCallback(() => {
    showApiKeyGroupMappingInfoDrawer();
  }, []);

  const handleManageGroupsClick = useCallback(() => {
    showApiKeysManageGroupsDrawer({
      apiKey,
      activationStatus,
    });
  }, [apiKey, activationStatus]);

  return (
    <>
      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" gap="medium">
          <PageInfoHeaderTitle title="Group mapping" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
        </Box>
        <PageInfoHeaderActions>
          {!!groups.length && (
            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.GROUPS}
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
          )}
          {canManageApiKeys && (
            <Button variant="primary" onPress={handleManageGroupsClick}>
              Manage groups
            </Button>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <TableContextProvider
        columnsConfig={columnsViewConfig}
        initialColumnViewConfig={initialColumnsViewConfig}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={API_KEY_GROUP_MAPPING_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
      >
        <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
          {!!filteredGroups.length && (
            <Table
              ariaLabel="Group mapping"
              items={filteredGroups}
              ref={virtualizedListContainerRef}
              loadingContent={loading}
            >
              {renderRow}
            </Table>
          )}
          {!groups.length && !loading && <ApiKeyGroupMappingEmpty />}
          {isPageEmpty && !!groups.length && (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          )}
        </TableWrapper>
      </TableContextProvider>
    </>
  );
};

export default ApiKeyGroupMapping;
